import React, { useState } from "react";
import { API } from "aws-amplify";
import {
  Box,
  TextField,
  Button,
  CircularProgress
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import QuestionCard from "./QuestionCard";
import PracticePageLayout, {
  inputSx,
  primaryButtonSx
} from "./PracticePageLayout";

export default function QuestionTailored() {
  const [jobDescription, setJobDescription] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [conversationText, setConversationText] = useState([]);
  const [blobURLs, setBlobURLs] = useState([]);
  const [audioKeys, setAudioKeys] = useState([]);
  const [responseTexts, setResponseTexts] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const handleInputChange = event => {
    setJobDescription(event.target.value);
  };
  const handlePrevQuestion = () => {
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  const handleNextQuestion = () => {
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };
  const handleBlobURLChange = (index, blobURL) => {
    const newBlobURLs = [...blobURLs];
    newBlobURLs[index] = blobURL;
    setBlobURLs(newBlobURLs);
  };

  const handleAudioKeyChange = (index, audioKey) => {
    const newAudioKeys = [...audioKeys];
    newAudioKeys[index] = audioKey;
    setAudioKeys(newAudioKeys);
  };

  const handleResponseTextChange = (index, responseText) => {
    const newResponseTexts = [...responseTexts];
    newResponseTexts[index] = responseText;
    setResponseTexts(newResponseTexts);
  };

  const handleFeedbackChange = (index, feedback) => {
    const newFeedbacks = [...feedbacks];
    newFeedbacks[index] = feedback;
    setFeedbacks(newFeedbacks);
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    let fullQuery = `Act as an interviewer for the following job posting ${jobDescription}, please generate ten questions an interviewee should expect in an interview for the position. Please format your response as a JSON with an array of ten strings, with each string containing a question. For example:

    {
      "questions": [
        //List of questions for the specific job posting
      ]
    }

    If there is nothing to say about a particular field, simply leave the array empty. Please make sure to use the exact field names provided (questions), and include double quotes around all strings. Do not include any additional text in your response
    `;
    const request = [{ role: "user", content: fullQuery }];
    try {
      await API.post("chatEndpoints", "/chat/completion", {
        body: request,
        headers: { "Content-Type": "application/json" }
      }).then(res => {
        const result = res.query;
        setConversationText(request, result);
        const json = JSON.parse(result.content);
        const newQuestions = json.questions.map(question => question);
        setQuestions(newQuestions);
        setBlobURLs(new Array(newQuestions.length).fill(""));
        setAudioKeys(new Array(newQuestions.length).fill(""));
        setResponseTexts(new Array(newQuestions.length).fill(""));
        setFeedbacks(new Array(newQuestions.length).fill(""));
        setLoading(false);
      });
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };
  const questionCards = questions.map((question, index) => {
    return (
      <QuestionCard
        key={index}
        question={question}
        index={index}
        total={questions.length}
        conversation={conversationText}
        blobURL={blobURLs[index]}
        audioKey={audioKeys[index]}
        responseText={responseTexts[index]}
        feedback={feedbacks[index]}
        onBlobURLChange={blobURL => handleBlobURLChange(index, blobURL)}
        onAudioKeyChange={audioKey => handleAudioKeyChange(index, audioKey)}
        onResponseTextChange={responseText =>
          handleResponseTextChange(index, responseText)
        }
        onFeedbackChange={feedback => handleFeedbackChange(index, feedback)}
        next={handleNextQuestion}
        prev={handlePrevQuestion}
      />
    );
  });

  return (
    <PracticePageLayout
      icon={<DescriptionOutlinedIcon />}
      kicker="From a job posting"
      title="Pull questions straight from a real job description."
      subtitle="Drop in a posting. We'll read the requirements and surface the questions you're most likely to face on the call."
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5
        }}
      >
        <TextField
          id="job-description"
          label="Job description"
          multiline
          rows={8}
          fullWidth
          variant="outlined"
          sx={inputSx}
          value={jobDescription}
          onChange={handleInputChange}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            variant="contained"
            sx={primaryButtonSx}
            onClick={handleSubmit}
            disabled={!jobDescription || isLoading}
          >
            {isLoading ? "Generating…" : "Generate questions"}
          </Button>
        </Box>
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : questions.length > 0 ? (
        <Box sx={{ mt: 4 }}>{questionCards[currentQuestionIndex]}</Box>
      ) : null}
    </PracticePageLayout>
  );
}
